// ─── Full ABIs extracted from Kavach contracts ───────────────────────────────
// Keep in sync with deployed contracts. Never invent functions not present on-chain.

export const ROUTER_ABI = [
  // Immutables
  "function factory() external view returns (address)",
  "function stableFactory() external view returns (address)",
  "function cpmmFactory() external view returns (address)",
  "function WETH9() external view returns (address)",
  "function AMM_AUTO() external view returns (uint8)",
  "function AMM_CPAMM() external view returns (uint8)",

  // V3 Exact-in single hop
  `function exactInputSingle((
    address tokenIn,
    address tokenOut,
    uint24 fee,
    address recipient,
    uint256 deadline,
    uint256 amountIn,
    uint256 amountOutMinimum,
    uint160 sqrtPriceLimitX96,
    uint8 ammKind
  ) params) external payable returns (uint256 amountOut)`,

  // V3 Exact-in multi-hop
  `function exactInput((
    bytes path,
    address recipient,
    uint256 deadline,
    uint256 amountIn,
    uint256 amountOutMinimum
  ) params) external payable returns (uint256 amountOut)`,

  // V3/CPMM/Stable Exact-out single hop
  `function exactOutputSingle((
    address tokenIn,
    address tokenOut,
    uint24 fee,
    address recipient,
    uint256 deadline,
    uint256 amountOut,
    uint256 amountInMaximum,
    uint160 sqrtPriceLimitX96,
    uint8 ammKind
  ) params) external payable returns (uint256 amountIn)`,
] as const;

export const V3_FACTORY_ABI = [
  "function createPool(address tokenA, address tokenB, uint24 fee) external payable returns (address pool)",
  "function getPool(address tokenA, address tokenB, uint24 fee) external view returns (address pool)",
  "function allPools(uint256 index) external view returns (address pool)",
  "function allPoolsLength() external view returns (uint256)",
  "function feeAmountTickSpacing(uint24 fee) external view returns (int24)",
  "function owner() external view returns (address)",
  "function feeSplitter() external view returns (address)",
  "function POOL_CREATION_FEE() external view returns (uint256)",
  "function collectProtocolFees(address pool) external returns (uint128 amount0, uint128 amount1)",
  "function setPoolFeeProtocol(address pool, uint8 fp0, uint8 fp1) external",
  "event PoolCreated(address indexed token0, address indexed token1, uint24 indexed fee, int24 tickSpacing, address pool)",
] as const;

export const V3_POOL_ABI = [
  // Immutables
  "function factory() external view returns (address)",
  "function token0() external view returns (address)",
  "function token1() external view returns (address)",
  "function fee() external view returns (uint24)",
  "function tickSpacing() external view returns (int24)",
  "function maxLiquidityPerTick() external view returns (uint128)",
  "function feeRecipient() external view returns (address)",

  // State
  "function slot0() external view returns (uint160 sqrtPriceX96, int24 tick, uint8 feeProtocol, bool unlocked)",
  "function feeGrowthGlobal0X128() external view returns (uint256)",
  "function feeGrowthGlobal1X128() external view returns (uint256)",
  "function protocolFees() external view returns (uint128 token0, uint128 token1)",
  "function liquidity() external view returns (uint128)",
  "function ticks(int24 tick) external view returns (uint128 liquidityGross, int128 liquidityNet, uint256 feeGrowthOutside0X128, uint256 feeGrowthOutside1X128, bool initialized)",
  "function positions(bytes32 key) external view returns (uint128 liquidity, uint256 feeGrowthInside0LastX128, uint256 feeGrowthInside1LastX128, uint128 tokensOwed0, uint128 tokensOwed1)",

  // Write
  "function initialize(uint160 sqrtPriceX96) external",
  "function mint(address recipient, int24 tickLower, int24 tickUpper, uint128 amount, bytes calldata data) external returns (uint256 amount0, uint256 amount1)",
  "function burn(int24 tickLower, int24 tickUpper, uint128 amount) external returns (uint256 amount0, uint256 amount1)",
  "function collect(address recipient, int24 tickLower, int24 tickUpper, uint128 amount0Requested, uint128 amount1Requested) external returns (uint128 amount0, uint128 amount1)",
  "function collectProtocol(uint128 amount0Requested, uint128 amount1Requested) external returns (uint128 amount0, uint128 amount1)",
  "function setFeeProtocol(uint8 feeProtocol0, uint8 feeProtocol1) external",
  "function swap(address recipient, bool zeroForOne, int256 amountSpecified, uint160 sqrtPriceLimitX96, bytes calldata data) external returns (int256 amount0, int256 amount1)",

  // Events
  "event Initialize(uint160 sqrtPriceX96, int24 tick)",
  "event Mint(address sender, address indexed owner, int24 indexed tickLower, int24 indexed tickUpper, uint128 amount, uint256 amount0, uint256 amount1)",
  "event Burn(address indexed owner, int24 indexed tickLower, int24 indexed tickUpper, uint128 amount, uint256 amount0, uint256 amount1)",
  "event Collect(address indexed owner, address recipient, int24 indexed tickLower, int24 indexed tickUpper, uint128 amount0, uint128 amount1)",
  "event Swap(address indexed sender, address indexed recipient, int256 amount0, int256 amount1, uint160 sqrtPriceX96, uint128 liquidity, int24 tick)",
] as const;

/** KavachFeeSplitter — protocol fee routing (40/30/20/10); distribute* is permissionless */
export const FEE_SPLITTER_ABI = [
  "function distributeETH() external",
  "function distributeToken(address token) external",
  "function distributeAll(address[] calldata tokens) external",
  "function stakingVault() external view returns (address)",
  "function treasury() external view returns (address)",
  "function miningVault() external view returns (address)",
  "function devFund() external view returns (address)",
] as const;

export const CPMM_FACTORY_ABI = [
  "function poolCreationFee() external view returns (uint256)",
  "function createPool(address tokenA, address tokenB, uint24 fee) external payable returns (address pool)",
  "function initializePool(address pool, uint256 amount0, uint256 amount1) external",
  "function getPool(address tokenA, address tokenB, uint24 fee) external view returns (address pool)",
  "function allPools(uint256 index) external view returns (address pool)",
  "function allPoolsLength() external view returns (uint256)",
  "function owner() external view returns (address)",
  "function feeSplitter() external view returns (address)",
  "event PoolCreated(address indexed token0, address indexed token1, uint24 indexed fee, address pool, uint256 poolId)",
] as const;

export const CPMM_POOL_ABI = [
  // Immutables
  "function token0() external view returns (address)",
  "function token1() external view returns (address)",
  "function fee() external view returns (uint24)",
  "function feeRecipient() external view returns (address)",
  "function factory() external view returns (address)",
  "function MINIMUM_LIQUIDITY() external view returns (uint256)",

  // State
  "function reserve0() external view returns (uint256)",
  "function reserve1() external view returns (uint256)",
  "function totalSupply() external view returns (uint256)",
  "function balanceOf(address account) external view returns (uint256)",
  "function protocolFees0() external view returns (uint256)",
  "function protocolFees1() external view returns (uint256)",
  "function initialized() external view returns (bool)",

  // Write
  "function mint(address to, uint256 amount0, uint256 amount1) external returns (uint256 liquidity)",
  "function burn(address to, uint256 liquidity) external returns (uint256 amount0, uint256 amount1)",
  "function swap(address tokenIn, address tokenOut, uint256 amountOutMin, address recipient) external returns (uint256 amountOut)",
  "function collect() external returns (uint256 amount0, uint256 amount1)",

  // View
  "function getAmountOut(uint256 amountIn, address tokenIn) external view returns (uint256 amountOut)",
  "function getAmountIn(uint256 amountOut, address tokenIn) external view returns (uint256 amountIn)",

  // Events
  "event Initialize(uint256 amount0, uint256 amount1)",
  "event Mint(address indexed sender, address indexed to, uint256 amount0, uint256 amount1, uint256 liquidity)",
  "event Burn(address indexed sender, address indexed to, uint256 liquidity, uint256 amount0, uint256 amount1)",
  "event Swap(address indexed sender, address indexed tokenIn, address indexed tokenOut, uint256 amountIn, uint256 amountOut, address recipient)",
  "event Collect(address indexed recipient, uint256 amount0, uint256 amount1)",
] as const;

export const STABLE_FACTORY_ABI = [
  "function createPool(address tokenA, address tokenB, uint256 A, uint256 fee, uint256 adminFee) external returns (address pool)",
  "function getPool(address tokenA, address tokenB) external view returns (address pool)",
  "function allPools(uint256 index) external view returns (address pool)",
  "function allPoolsLength() external view returns (uint256)",
  "function owner() external view returns (address)",
  "event PoolCreated(address indexed token0, address indexed token1, address pool)",
] as const;

export const STABLE_POOL_ABI = [
  // Immutables
  "function factory() external view returns (address)",
  "function token0() external view returns (address)",
  "function token1() external view returns (address)",
  "function precisionMultiplier0() external view returns (uint256)",
  "function precisionMultiplier1() external view returns (uint256)",

  // State
  "function balances0() external view returns (uint256)",
  "function balances1() external view returns (uint256)",
  "function fee() external view returns (uint256)",
  "function adminFee() external view returns (uint256)",
  "function A() external view returns (uint256)",
  "function feeSplitter() external view returns (address)",
  "function isKilled() external view returns (bool)",

  // ERC20 LP token
  "function totalSupply() external view returns (uint256)",
  "function balanceOf(address account) external view returns (uint256)",
  "function transfer(address to, uint256 amount) external returns (bool)",
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",

  // Write — liquidity
  "function addLiquidity(uint256[2] memory amounts, uint256 minMintAmount) external returns (uint256)",
  "function removeLiquidity(uint256 lpAmount, uint256[2] memory minAmounts) external returns (uint256[2] memory amounts)",
  "function removeLiquidityOneCoin(uint256 lpAmount, uint8 i, uint256 minAmount) external returns (uint256)",

  // Write — swap
  "function swap(uint8 i, uint8 j, uint256 minDy) external returns (uint256 dy)",

  // View — quotes
  "function getDy(uint8 i, uint8 j, uint256 dx) external view returns (uint256 dy)",
  "function getDx(uint8 i, uint8 j, uint256 dy) external view returns (uint256 dx)",
  "function getVirtualPrice() external view returns (uint256)",

  // Events
  "event TokenExchange(address indexed buyer, uint8 i, uint256 dx, uint8 j, uint256 dy)",
  "event AddLiquidity(address indexed provider, uint256[2] amounts, uint256[2] fees, uint256 invariant, uint256 supply)",
  "event RemoveLiquidity(address indexed provider, uint256[2] amounts, uint256 supply)",
] as const;

export const ERC20_ABI = [
  "function name() external view returns (string)",
  "function symbol() external view returns (string)",
  "function decimals() external view returns (uint8)",
  "function totalSupply() external view returns (uint256)",
  "function balanceOf(address account) external view returns (uint256)",
  "function transfer(address to, uint256 amount) external returns (bool)",
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function transferFrom(address from, address to, uint256 amount) external returns (bool)",
  "event Transfer(address indexed from, address indexed to, uint256 value)",
  "event Approval(address indexed owner, address indexed spender, uint256 value)",
] as const;

// KavachV3Periphery — production V3 add/remove/collect (positions held by periphery; user is beneficiary)
export const V3_PERIPHERY_ABI = [
  `function mint((address token0, address token1, uint24 fee, int24 tickLower, int24 tickUpper, uint128 liquidity, uint256 amount0Max, uint256 amount1Max, address recipient, uint256 deadline) params) external returns (uint256 amount0, uint256 amount1)`,
  `function decreaseLiquidity((address token0, address token1, uint24 fee, int24 tickLower, int24 tickUpper, uint128 liquidity, uint256 amount0Min, uint256 amount1Min, address recipient, uint256 deadline) params) external returns (uint256 amount0, uint256 amount1)`,
  `function collect((address token0, address token1, uint24 fee, int24 tickLower, int24 tickUpper, uint128 amount0Max, uint128 amount1Max, address recipient, uint256 deadline) params) external returns (uint128 amount0, uint128 amount1)`,
] as const;

// KavachV3MintHelper ABI — legacy thin helper (optional; prefer v3Periphery = KavachV3Periphery)
// See periphery/KavachV3MintHelper.sol
export const V3_MINT_HELPER_ABI = [
  `function mint(
    address pool,
    address recipient,
    int24 tickLower,
    int24 tickUpper,
    uint128 amount,
    uint256 amount0Max,
    uint256 amount1Max
  ) external returns (uint256 amount0, uint256 amount1)`,
] as const;

// KavachCpmmLiqHelper ABI — periphery contract for CPMM liquidity
// See periphery/KavachCpmmLiqHelper.sol
export const CPMM_LIQ_HELPER_ABI = [
  `function addLiquidity(
    address pool,
    uint256 amount0Desired,
    uint256 amount1Desired,
    uint256 minLiquidity,
    address recipient
  ) external returns (uint256 liquidity, uint256 amount0Used, uint256 amount1Used)`,
  `function addInitialLiquidity(
    address factory,
    address pool,
    uint256 amount0,
    uint256 amount1,
    address recipient
  ) external returns (uint256 liquidity)`,
] as const;
